import { PatternLocationActions } from './PatternLocationActions';
import { defaultManufacturingSeason } from "@/lib/manufacturingSeasons";
import { SeasonSelect } from "@/components/ui/season-select";
import React from "react";
import { ProductPhotoCatalogDialog } from "./ProductPhotoCatalogDialog";
import { AlertTriangle, Copy, Edit, Image as ImageIcon, Lock, MessageSquare, Plus, RefreshCw, Save, Search, Star, StarOff, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/components/ui/use-toast";
import { CostSheetManager } from "@/components/manufacturing/CostSheetManager";
import { ProductSimulationsGrid } from "@/components/manufacturing/ProductSimulationsGrid";
import {
  calculateSalePricesFromFamilies,
  deleteManufacturingProduct,
  duplicateManufacturingProduct,
  fetchCostSheets,
  fetchFamilies,
  fetchGarmentTypes,
  fetchManufacturingProducts,
  fetchSeasons,
  formatMoney,
  getNextFamilyProductCode,
  LEGACY_SALE_PRICE_FIELDS,
  describeFamilyTemplateSeed,
  previewDuplicateManufacturingProduct,
  recalculateProductSalePrices,
  salePriceMismatches,
  saveManufacturingProduct,
  updateProductSaleTariff,
  updateManufacturingProductObservations,
} from "@/lib/api/manufacturing";
import { DUPLICATE_FAMILY_RULES, preservedReferenceProductCode, canPreserveDuplicateReference, productCodeWithSeason, productCodeWithoutSeason, groupDuplicateIssues, shouldPreviewDuplicateFamilyChange, specialSizesProductCode } from "@/lib/manufacturingDuplicate";
import { MFG_PRODUCT_STATUS_LABELS } from "@/lib/constants";
import { pendingChecks, pendingStages, productCostSheet, WORKFLOW_LABELS } from "@/lib/costSheetPending";

const emptyForm = {
  code: "",
  description: "",
  cod_fam: "",
  temporada: "",
  composicion: "",
  garment_type_id: "",
  producto_estrella: false,
  observaciones: "",
  codigo_patron: "",
  ubicacion_patron: "",
  status: "active",
  images: [],
  main_image_index: 0,
  precio_v: 0,
  precio_v1: 0,
  precio_v2: 0,
  precio_v3: 0,
  precio_v4: 0,
  precio_v5: 0,
};

function formFromProduct(product = {}) {
  return {
    code: product.code || "",
    description: product.description || "",
    cod_fam: product.cod_fam?.trim() || "",
    temporada: product.temporada || "",
    composicion: product.composicion || "",
    garment_type_id: product.garment_type_id || "",
    producto_estrella: Boolean(product.producto_estrella),
    observaciones: product.observaciones || "",
    codigo_patron: product.codigo_patron || "",
    ubicacion_patron: product.ubicacion_patron || "",
    status: product.status || "active",
    images: Array.isArray(product.images) ? product.images : [],
    main_image_index: product.main_image_index || 0,
    precio_v: product.precio_v || 0,
    precio_v1: product.precio_v1 || 0,
    precio_v2: product.precio_v2 || 0,
    precio_v3: product.precio_v3 || 0,
    precio_v4: product.precio_v4 || 0,
    precio_v5: product.precio_v5 || 0,
  };
}

const statusLabels = MFG_PRODUCT_STATUS_LABELS;

const salePriceLabels = ["Tarifa 1", "Tarifa 2", "Tarifa 3", "Tarifa 4", "Tarifa 5", "Tarifa 6"];

const duplicateIssueToneClass = {
  danger: "border-red-200 bg-red-50 text-red-950",
  warning: "border-yellow-200 bg-yellow-50 text-yellow-950",
  info: "border-blue-200 bg-blue-50 text-blue-950",
};

function DuplicateIssueGroups({ issues }) {
  const groups = groupDuplicateIssues(issues);
  if (!groups.length) return null;
  return (
    <div className="max-h-[50vh] space-y-3 overflow-y-auto pr-1">
      {groups.map((group) => (
        <div key={group.id} className={`rounded-md border p-3 text-sm ${duplicateIssueToneClass[group.tone] || duplicateIssueToneClass.warning}`}>
          <div className="font-semibold">{group.title}</div>
          {group.description && <p className="mt-1 text-xs opacity-80">{group.description}</p>}
          <ul className="mt-2 list-disc space-y-1 pl-4 text-sm">
            {group.items.map((item, index) => (
              <li key={`${item.code}-${item.label}-${index}`}>{item.label}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

const optimizeImage = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let { width, height } = img;
        const maxWidth = 800;
        const maxHeight = 800;

        if (width > height && width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        } else if (height > maxHeight) {
          width = Math.round((width * maxHeight) / height);
          height = maxHeight;
        }

        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);

        let quality = 0.7;
        let dataUrl = canvas.toDataURL("image/jpeg", quality);
        while (dataUrl.length > 50 * 1024 && quality > 0.1) {
          quality -= 0.1;
          dataUrl = canvas.toDataURL("image/jpeg", quality);
        }
        resolve(dataUrl);
      };
      img.onerror = reject;
    };
    reader.onerror = reject;
  });

function getMainImage(product) {
  const images = Array.isArray(product.images) ? product.images : [];
  return images[product.main_image_index || 0] || images[0] || null;
}

export function ManufacturingProductManager() {
  const [catalogFilters, setCatalogFilters] = React.useState(null);
  const [magnetFilters, setMagnetFilters] = React.useState(null);
  const [products, setProducts] = React.useState([]);
  const [garmentTypes, setGarmentTypes] = React.useState([]);
  const [garmentTypeFilter, setGarmentTypeFilter] = React.useState("");
  const [families, setFamilies] = React.useState([]);
  const [seasons, setSeasons] = React.useState([]);
  const [appliedFilters, setAppliedFilters] = React.useState(null);
  const searchTerm = appliedFilters?.searchTerm || "";
  const [searchDraft, setSearchDraft] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("active");
  const [temporadaFilter, setTemporadaFilter] = React.useState("");
  const [familyFilter, setFamilyFilter] = React.useState("");
  const [starFilter, setStarFilter] = React.useState("");
  const [observationsTarget, setObservationsTarget] = React.useState(null);
  const [observationsDraft, setObservationsDraft] = React.useState("");
  const [savingObservations, setSavingObservations] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(false);
  const [sheetStageFilter, setSheetStageFilter] = React.useState("");
  const beforeCloseRef = React.useRef(null);
  const initialFormRef = React.useRef({});
  const savingRef = React.useRef(false);
  const createRequestRef = React.useRef(null);
  const duplicateRequestRef = React.useRef(null);
  const duplicateCodeRequestRef = React.useRef(0);
  const duplicateStandardDraftRef = React.useRef(null);
  const [saving, setSaving] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [formData, setFormData] = React.useState(emptyForm);
  const [loading, setLoading] = React.useState(false);
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [imagePreview, setImagePreview] = React.useState(null);
  const [productRevision, setProductRevision] = React.useState(0);
  const [seasonsLoading, setSeasonsLoading] = React.useState(true);
  const [newCodeLoading, setNewCodeLoading] = React.useState(false);
  const [duplicateSource, setDuplicateSource] = React.useState(null);
  const [duplicateCodFam, setDuplicateCodFam] = React.useState("");
  const [duplicateSeason, setDuplicateSeason] = React.useState("");
  const [duplicateCode, setDuplicateCode] = React.useState("");
  const [duplicateCodeLoading, setDuplicateCodeLoading] = React.useState(false);
  const [duplicating, setDuplicating] = React.useState(false);
  const [duplicateStep, setDuplicateStep] = React.useState("form");
  const [duplicatePreview, setDuplicatePreview] = React.useState(null);
  const [duplicatePreviewLoading, setDuplicatePreviewLoading] = React.useState(false);
  const [duplicateKeepReference, setDuplicateKeepReference] = React.useState(false);
  const [duplicateSpecialSizes, setDuplicateSpecialSizes] = React.useState(false);
  const [sheetValidated, setSheetValidated] = React.useState(false);
  const [recalculating, setRecalculating] = React.useState(false);
  const [sheetTotalCost, setSheetTotalCost] = React.useState(0);
  const productsRequestIdRef = React.useRef(0);
  const newCodeRequestIdRef = React.useRef(0);
  const { toast } = useToast();

  const handleValidationChange = React.useCallback((validated) => {
    setSheetValidated(validated);
    setProducts((current) => current.map((product) => product.id === editingId
      ? { ...product, cost_sheet: { ...productCostSheet(product), validation_status: validated ? "validated" : null } }
      : product));
  }, [editingId]);

  const loadProducts = React.useCallback(async () => {
    if (!appliedFilters) return;
    const requestId = productsRequestIdRef.current + 1;
    productsRequestIdRef.current = requestId;
    setLoading(true);
    const result = await fetchManufacturingProducts({
      ...appliedFilters,
      includePendingChecks: true,
      limit: 251,
      offset: page * 250,
    });
    if (requestId !== productsRequestIdRef.current) return;
    if (result.error) {
      toast({ title: "Error", description: "No se pudieron cargar los articulos", variant: "destructive" });
    }
    setProducts((result.data || []).slice(0, 250));
    setHasMore((result.data || []).length > 250);
    setLoading(false);
  }, [appliedFilters, page, toast]);

  React.useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  React.useEffect(() => {
    const loadFamilies = async () => {
      const result = await fetchFamilies();
      if (result.error) {
        toast({ title: "Error", description: "No se pudieron cargar las familias", variant: "destructive" });
        return;
      }
      setFamilies(result.data || []);
    };

    loadFamilies();
  }, [toast]);

  React.useEffect(() => {
    const loadSeasons = async () => {
      const result = await fetchSeasons();
      setSeasonsLoading(false);
      if (result.error) {
        toast({ title: "Error", description: "No se pudieron cargar las temporadas", variant: "destructive" });
        return;
      }
      setSeasons(result.data || []);
    };

    loadSeasons();
  }, [toast]);

  React.useEffect(() => {
    fetchGarmentTypes().then((result) => {
      if (result.error) toast({ title: "Error", description: "No se pudieron cargar los tipos de prenda", variant: "destructive" });
      else setGarmentTypes(result.data || []);
    });
  }, [toast]);

  const applySearch = React.useCallback(() => {
    const normalizedSearch = searchDraft.trim();
    setSearchDraft(normalizedSearch);
    setPage(0);
    setAppliedFilters({
      searchTerm: normalizedSearch,
      status: statusFilter,
      temporada: temporadaFilter,
      codFam: familyFilter,
      sheetStage: sheetStageFilter,
      starFilter,
      garmentType: garmentTypeFilter,
    });
  }, [searchDraft, statusFilter, temporadaFilter, familyFilter, sheetStageFilter, starFilter, garmentTypeFilter]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    applySearch();
  };

  const clearListFilters = () => {
    setSearchDraft("");
    setTemporadaFilter("");
    setFamilyFilter("");
    setSheetStageFilter("");
    setStarFilter("");
    setGarmentTypeFilter("");
  };

  const reset = () => {
    createRequestRef.current = null;
    newCodeRequestIdRef.current += 1;
    setEditingId(null);
    setFormData({ ...emptyForm, images: [], temporada: defaultManufacturingSeason(seasons) });
    setNewCodeLoading(false);
    setSheetValidated(false);
    setRecalculating(false);
    setSheetTotalCost(0);
  };

  const openNew = () => {
    reset();
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setSaving(false);
    reset();
    loadProducts();
  };

  const persistProduct = async () => {
    if (sheetValidated) {
      toast({
        title: "Artículo validado",
        description: "Quita la validación del escandallo para poder modificar la ficha.",
        variant: "destructive",
      });
      return false;
    }
    if (!editingId && !formData.cod_fam.trim()) {
      toast({ title: "Validacion", description: "Selecciona una familia para generar el codigo", variant: "destructive" });
      return false;
    }
    if (!editingId && !formData.temporada.trim()) {
      toast({ title: "Validacion", description: "La temporada es obligatoria", variant: "destructive" });
      return false;
    }
    if (!editingId && newCodeLoading) {
      toast({ title: "Validacion", description: "Espera a que se genere el codigo automatico", variant: "destructive" });
      return false;
    }
    if (!formData.code.trim() || !formData.description.trim()) {
      toast({ title: "Validacion", description: "Codigo y descripcion son obligatorios", variant: "destructive" });
      return false;
    }
    if (formData.temporada.trim().length > 4) {
      toast({ title: "Validacion", description: "El codigo de temporada no puede superar 4 caracteres", variant: "destructive" });
      return false;
    }

    if (savingRef.current) return false;
    savingRef.current = true;
    setSaving(true);
    if (beforeCloseRef.current && !(await beforeCloseRef.current())) { savingRef.current = false; setSaving(false); return false; }
    createRequestRef.current ||= crypto.randomUUID();
    const result = await saveManufacturingProduct(
      {
        code: formData.code,
        description: formData.description,
        cod_fam: formData.cod_fam,
        temporada: formData.temporada,
        composicion: formData.composicion,
        garment_type_id: formData.garment_type_id,
        producto_estrella: formData.producto_estrella,
        observaciones: formData.observaciones,
        codigo_patron: formData.codigo_patron,
        ubicacion_patron: formData.ubicacion_patron,
        status: formData.status || "active",
        images: formData.images,
        main_image_index: formData.main_image_index,
        ...Object.fromEntries(LEGACY_SALE_PRICE_FIELDS.filter((field) => !editingId || Number(formData[field]) !== Number(initialFormRef.current[field])).map((field) => [field, formData[field]])),
      },
      editingId,
      { requestId: createRequestRef.current }
    );
    savingRef.current = false;
    setSaving(false);

    if (result.error) {
      toast({ title: "Error", description: result.error.message || "No se pudo guardar el articulo", variant: "destructive" });
      return false;
    }

    toast({
      title: editingId ? "Articulo actualizado" : "Articulo creado",
      description: editingId ? undefined : describeFamilyTemplateSeed(result) || undefined,
    });
    newCodeRequestIdRef.current += 1;
    setNewCodeLoading(false);
    if (result.data?.id) {
      setEditingId(result.data.id);
      setProductRevision((current) => current + 1);
      initialFormRef.current = formFromProduct(result.data);
      setFormData(formFromProduct(result.data));
    }
    await loadProducts();
    return true;
  };

  const handleEditorOpenChange = async (open) => {
    if (savingRef.current) return;
    if (open) {
      setEditorOpen(true);
      return;
    }
    if (editingId && !sheetValidated) {
      const ok = await persistProduct();
      if (!ok) return;
    }
    closeEditor();
  };

  const edit = (product) => {
    newCodeRequestIdRef.current += 1;
    setNewCodeLoading(false);
    setEditingId(product.id);
    initialFormRef.current = formFromProduct(product);
    setFormData(formFromProduct(product));
    setSheetValidated(false);
    setEditorOpen(true);
    fetchCostSheets(product.id).then((result) => {
      setSheetValidated(result.data?.[0]?.validation_status === "validated");
      setSheetTotalCost(Number(result.data?.[0]?.totals?.total || 0));
    });
  };

  const submit = async (event) => {
    event.preventDefault();
    await persistProduct();
  };

  const selectedFamily = families.find((family) => family.cod_fam?.trim() === formData.cod_fam?.trim());
  const highlightedTariff = Number(selectedFamily?.tarifa) || null;
  const expectedPrices = React.useMemo(
    () => calculateSalePricesFromFamilies(sheetTotalCost, families, formData.cod_fam),
    [sheetTotalCost, families, formData.cod_fam]
  );
  const mismatchByTariff = React.useMemo(() => {
    if (!editingId) return {};
    return Object.fromEntries(salePriceMismatches(formData, expectedPrices).map((item) => [item.tarifa, item]));
  }, [editingId, formData, expectedPrices]);

  const applyAutomaticPrices = React.useCallback((cost) => {
    const prices = calculateSalePricesFromFamilies(cost, families, formData.cod_fam);
    setFormData((current) => ({ ...current, ...prices }));
    // Automatic prices are persisted with cost-line changes, never submitted from a stale form.
    initialFormRef.current = { ...initialFormRef.current, ...prices };
  }, [families, formData.cod_fam]);

  const applyRecalculatedProduct = async (result, successTitle) => {
    if (result.error) {
      toast({
        title: "Error",
        description: result.error.message || "No se pudieron recalcular las tarifas",
        variant: "destructive",
      });
      return false;
    }
    setFormData(formFromProduct(result.data));
    toast({ title: successTitle });
    await loadProducts();
    return true;
  };

  const recalculatePrices = async () => {
    if (!editingId || sheetValidated || recalculating) return;
    setRecalculating(true);
    const result = await recalculateProductSalePrices(editingId);
    setRecalculating(false);
    await applyRecalculatedProduct(result, "Tarifas recalculadas");
  };

  const updateSingleTariff = async (tarifa) => {
    if (!editingId || sheetValidated || recalculating) return;
    setRecalculating(true);
    const result = await updateProductSaleTariff(editingId, tarifa);
    setRecalculating(false);
    await applyRecalculatedProduct(result, `Tarifa ${tarifa} actualizada`);
  };

  const generateNewProductCode = async (codFam, season = formData.temporada) => {
    const requestId = newCodeRequestIdRef.current + 1;
    newCodeRequestIdRef.current = requestId;

    if (!codFam || !season) {
      setFormData((current) => ({ ...current, code: "" }));
      setNewCodeLoading(false);
      return;
    }

    setNewCodeLoading(true);
    const result = await getNextFamilyProductCode(codFam, season);
    if (requestId !== newCodeRequestIdRef.current) return;
    setNewCodeLoading(false);

    if (result.error) {
      toast({
        title: "Error",
        description: result.error.message || "No se pudo generar el codigo",
        variant: "destructive",
      });
      setFormData((current) => ({ ...current, code: "" }));
      return;
    }

    setFormData((current) => ({ ...current, code: result.data || "" }));
  };

  const changeFormFamily = async (codFam) => {
    setFormData((current) => ({
      ...current,
      cod_fam: codFam,
      code: editingId ? current.code : "",
    }));
    if (!editingId) await generateNewProductCode(codFam);
  };

  const changeFormSeason = async (season) => {
    setFormData((current) => ({ ...current, temporada: season,
      code: editingId ? current.code : productCodeWithSeason(current.code, season, current.temporada) }));
    if (!editingId) {
      newCodeRequestIdRef.current += 1;
      setNewCodeLoading(false);
      if (!formData.code || newCodeLoading) await generateNewProductCode(formData.cod_fam, season);
    }
  };

  const remove = async (product) => {
    const sheets = await fetchCostSheets(product.id);
    if (sheets.data?.[0]?.validation_status === "validated") {
      toast({
        title: "Artículo validado",
        description: "Quita la validación del escandallo para poder eliminar el artículo.",
        variant: "destructive",
      });
      return;
    }
    const deleteMessage = [
      `¿Eliminar el articulo "${product.code} - ${product.description}"?`,
      "Se borraran tambien sus escandallos y documentos tecnicos.",
      "Si esta usado en simulaciones, pedidos u ordenes de fabricacion, no se eliminara.",
    ].join(" ");
    if (!window.confirm(deleteMessage)) return;
    const result = await deleteManufacturingProduct(product.id);
    if (result.error) {
      toast({
        title: "Error",
        description: result.error.message || "No se pudo eliminar el articulo. Puede estar usado en simulaciones, pedidos u ordenes de fabricacion.",
        variant: "destructive",
      });
      return;
    }
    toast({ title: "Articulo eliminado" });
    if (editingId === product.id) reset();
    await loadProducts();
  };

  const generateDuplicateCode = async (codFam, season = duplicateSeason) => {
    const request = ++duplicateCodeRequestRef.current;
    if (!codFam || !season) {
      setDuplicateCodeLoading(false);
      setDuplicateCode("");
      return;
    }
    setDuplicateCodeLoading(true);
    const result = await getNextFamilyProductCode(codFam, season);
    if (request !== duplicateCodeRequestRef.current) return;
    setDuplicateCodeLoading(false);
    if (result.error) {
      toast({
        title: "Error",
        description: result.error.message || "No se pudo generar el código",
        variant: "destructive",
      });
      setDuplicateCode("");
      return;
    }
    setDuplicateCode(result.data || "");
  };

  const openDuplicate = async (product) => {
    duplicateStandardDraftRef.current = null;
    setDuplicateSource(product);
    const codFam = product.cod_fam?.trim() || "";
    const season = defaultManufacturingSeason(seasons);
    setDuplicateCodFam(codFam);
    setDuplicateCode("");
    setDuplicateStep("form");
    setDuplicatePreview(null);
    setDuplicateSpecialSizes(false);
    setDuplicateKeepReference(false);
    setDuplicateSeason(season);
    setDuplicateCodeLoading(false);
    duplicateCodeRequestRef.current += 1;
    if (codFam && season) await generateDuplicateCode(codFam, season);
  };

  const closeDuplicate = () => {
    duplicateStandardDraftRef.current = null;
    duplicateCodeRequestRef.current += 1;
    duplicateRequestRef.current = null;
    setDuplicateSource(null);
    setDuplicateSeason("");
    setDuplicateCodFam("");
    setDuplicateCode("");
    setDuplicateCodeLoading(false);
    setDuplicateStep("form");
    setDuplicatePreview(null);
    setDuplicatePreviewLoading(false);
    setDuplicateSpecialSizes(false);
    setDuplicateKeepReference(false);
  };

  const changeDuplicateFamily = async (codFam) => {
    if (duplicateSpecialSizes || duplicateKeepReference) return;
    setDuplicateCodFam(codFam);
    setDuplicateStep("form");
    setDuplicatePreview(null);
    await generateDuplicateCode(codFam);
  };

  const changeDuplicateSeason = async (season) => {
    setDuplicateSeason(season);
    setDuplicateStep("form");
    setDuplicatePreview(null);
    duplicateCodeRequestRef.current += 1;
    setDuplicateCodeLoading(false);
    const draft = duplicateStandardDraftRef.current;
    if (draft && season) {
      draft.code = productCodeWithSeason(draft.code, season, draft.season);
      draft.season = season;
    }
    if (duplicateKeepReference) {
      setDuplicateCode(preservedReferenceProductCode(duplicateSource.code, families.find((family) => family.cod_fam.trim() === duplicateSource.cod_fam?.trim()), season));
    } else if (duplicateSpecialSizes) {
      setDuplicateCode(specialSizesProductCode(duplicateSource.code, duplicateSource.temporada, season));
    } else if (duplicateCode && !duplicateCodeLoading) {
      setDuplicateCode(productCodeWithSeason(duplicateCode, season, duplicateSeason));
    } else {
      await generateDuplicateCode(duplicateCodFam, season);
    }
  };

  const toggleDuplicateSpecialSizes = (checked) => {
    if (!duplicateSource || duplicateCodeLoading || !duplicateSeason || duplicateKeepReference) return;
    if (checked && productCodeWithoutSeason(duplicateSource?.code, duplicateSource?.temporada).endsWith("/E")) {
      toast({ title: "El artículo ya es de tallas especiales", variant: "destructive" });
      return;
    }
    setDuplicateSpecialSizes(checked);
    setDuplicateStep("form");
    setDuplicatePreview(null);
    if (checked) {
      duplicateStandardDraftRef.current = { code: duplicateCode, codFam: duplicateCodFam, season: duplicateSeason };
      const sourceFam = duplicateSource.cod_fam?.trim() || "";
      setDuplicateCodFam(sourceFam);
      setDuplicateCode(specialSizesProductCode(duplicateSource.code, duplicateSource.temporada, duplicateSeason));
      return;
    }
    const draft = duplicateStandardDraftRef.current;
    if (draft) {
      setDuplicateCodFam(draft.codFam);
      setDuplicateCode(draft.code);
    }
    duplicateStandardDraftRef.current = null;
  };

  const toggleDuplicateKeepReference = (checked) => {
    if (!duplicateSource || duplicateSpecialSizes || duplicateCodeLoading) return;
    if (checked && !canPreserveDuplicateReference(duplicateSource.cod_fam, duplicateCodFam)) return;
    setDuplicateKeepReference(checked);
    setDuplicateStep("form");
    setDuplicatePreview(null);
    if (checked) {
      duplicateStandardDraftRef.current = { code: duplicateCode, codFam: duplicateCodFam, season: duplicateSeason };
      setDuplicateCode(preservedReferenceProductCode(duplicateSource.code, families.find((family) => family.cod_fam.trim() === duplicateSource.cod_fam?.trim()), duplicateSeason));
      return;
    }
    const draft = duplicateStandardDraftRef.current;
    setDuplicateCode(duplicateSeason ? draft?.code || "" : "");
    duplicateStandardDraftRef.current = null;
    if (duplicateSeason && !draft?.code) generateDuplicateCode(duplicateCodFam);
  };

  const startDuplicate = async () => {
    if (!duplicateSource) return;
    if (!duplicateSeason || duplicateCodeLoading) {
      toast({ title: "Validación", description: "Selecciona una temporada y espera a que se genere el código", variant: "destructive" });
      return;
    }
    if (!duplicateCode.trim()) {
      toast({ title: "Validación", description: "Indica un código para el nuevo artículo", variant: "destructive" });
      return;
    }
    if (!shouldPreviewDuplicateFamilyChange(duplicateSource.cod_fam, duplicateCodFam)) {
      await confirmDuplicate();
      return;
    }
    setDuplicatePreviewLoading(true);
    const result = await previewDuplicateManufacturingProduct(duplicateSource, { codFam: duplicateCodFam });
    setDuplicatePreviewLoading(false);
    if (result.error) {
      toast({
        title: "Error",
        description: result.error.message || "No se pudo revisar la duplicación",
        variant: "destructive",
      });
      return;
    }
    if (!result.data?.issues?.length) {
      await confirmDuplicate();
      return;
    }
    setDuplicatePreview(result.data);
    setDuplicateStep("review");
  };

  const confirmDuplicate = async () => {
    if (!duplicateSource) return;
    if (!duplicateSeason || duplicateCodeLoading) {
      toast({ title: "Validación", description: "Selecciona una temporada y espera a que se genere el código", variant: "destructive" });
      return;
    }
    if (!duplicateCode.trim()) {
      toast({ title: "Validación", description: "Indica un código para el nuevo artículo", variant: "destructive" });
      return;
    }
    duplicateRequestRef.current ||= crypto.randomUUID();
    setDuplicating(true);
    const result = await duplicateManufacturingProduct(duplicateSource, {
      code: duplicateCode,
      keepReference: duplicateKeepReference,
      temporada: duplicateSeason,
      codFam: duplicateCodFam,
      specialSizes: duplicateSpecialSizes,
      requestId: duplicateRequestRef.current,
    });
    setDuplicating(false);
    if (result.error) {
      toast({
        title: "Error",
        description: result.error.message || "No se pudo duplicar el artículo",
        variant: "destructive",
      });
      return;
    }
    const createdProduct = result.data;
    if (result.warning) {
      toast({ title: "Artículo duplicado con avisos", description: result.warning });
    } else {
      toast({ title: "Artículo duplicado", description: `Se creó ${createdProduct?.code || duplicateCode}` });
    }
    closeDuplicate();
    await loadProducts();
    if (createdProduct?.id) edit(createdProduct);
  };

  const uploadImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || sheetValidated) return;

    try {
      const image = await optimizeImage(file);
      setFormData((current) => ({
        ...current,
        images: [...current.images, image],
      }));
    } catch (error) {
      console.error("Error optimizing manufacturing product image:", error);
      toast({ title: "Error", description: "No se pudo procesar la imagen", variant: "destructive" });
    }
  };

  const removeImage = (index) => {
    if (sheetValidated) return;
    if (!window.confirm("¿Quieres eliminar esta imagen del artículo?")) return;
    setFormData((current) => {
      const images = current.images.filter((_, imageIndex) => imageIndex !== index);
      const nextMainIndex = current.main_image_index >= index ? Math.max(0, current.main_image_index - 1) : current.main_image_index;
      return {
        ...current,
        images,
        main_image_index: Math.min(nextMainIndex, Math.max(images.length - 1, 0)),
      };
    });
  };

  const setMainImage = (index) => {
    if (sheetValidated) return;
    setFormData((current) => ({ ...current, main_image_index: index }));
  };

  const openObservations = (product) => {
    setObservationsDraft(product.observaciones || "");
    setObservationsTarget({ product });
  };

  const saveObservations = async () => {
    if (savingObservations) return;
    setSavingObservations(true);
    try {
      const result = await updateManufacturingProductObservations(observationsTarget.product.id, observationsDraft);
      if (result.error) throw result.error;
      setObservationsTarget(null);
      toast({ title: "Observaciones guardadas" });
      await loadProducts();
    } catch (error) {
      toast({ title: "Error", description: error.message || "No se pudieron guardar las observaciones", variant: "destructive" });
    } finally {
      setSavingObservations(false);
    }
  };

  const hasPendingSearch = !appliedFilters
    || searchDraft.trim() !== searchTerm
    || statusFilter !== appliedFilters.status
    || temporadaFilter !== appliedFilters.temporada
    || familyFilter !== appliedFilters.codFam
    || sheetStageFilter !== appliedFilters.sheetStage
    || starFilter !== appliedFilters.starFilter
    || garmentTypeFilter !== appliedFilters.garmentType;
  const hasListFilters = Boolean(searchTerm || searchDraft.trim() || temporadaFilter || familyFilter || sheetStageFilter || starFilter || garmentTypeFilter);

  return (
    <div className="space-y-4">
      {magnetFilters && <ProductPhotoCatalogDialog magnetic initialFilters={magnetFilters} families={families} seasons={seasons} garmentTypes={garmentTypes} onClose={() => setMagnetFilters(null)} />}
      {catalogFilters && <ProductPhotoCatalogDialog initialFilters={catalogFilters} families={families} seasons={seasons} garmentTypes={garmentTypes} onClose={() => setCatalogFilters(null)} />}
      <div className="rounded-lg border bg-white p-4 shadow-sm">
        <form onSubmit={handleSearchSubmit} className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
          <div className="relative">
            <Input
              type="search"
              placeholder="Buscar por codigo o descripcion..."
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
              className="pl-9"
            />
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          </div>
          <Button type="submit" variant="outline" disabled={!hasPendingSearch || loading}>
            <Search className="mr-2 h-4 w-4" />
            Aplicar
          </Button>
          <select
            className="rounded-md border border-input bg-background px-3 py-2"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="active">Activos</option>
            <option value="obsolete">Obsoletos</option>
            <option value="all">Todos</option>
          </select>
          <SeasonSelect data-season-selector="true"
            className="product-season-filter rounded-md border border-input bg-background px-3 py-2"
            value={temporadaFilter}
            onChange={(event) => setTemporadaFilter(event.target.value)}
          >
            <option value="">Todas las temporadas</option>
            {seasons.map((season) => (
              <option key={season.cod_tem} value={season.cod_tem.trim()}>
                {season.cod_tem.trim()} · {season.descrip}
              </option>
            ))}
          </SeasonSelect>
          <select
            className="rounded-md border border-input bg-background px-3 py-2"
            value={familyFilter}
            onChange={(event) => setFamilyFilter(event.target.value)}
          >
            <option value="">Todas las familias</option>
            {families.map((family) => (
              <option key={family.cod_fam} value={family.cod_fam.trim()}>
                {family.cod_fam.trim()} · {family.descrip}
              </option>
            ))}
          </select>
          <select
            aria-label="Estado de escandallo"
            className="rounded-md border border-input bg-background px-3 py-2"
            value={sheetStageFilter}
            onChange={(event) => setSheetStageFilter(event.target.value)}
          >
            <option value="">Escandallo: todos</option>
            <option value="design">Diseño</option>
            <option value="pattern">Patronaje</option>
            <option value="production">Producción</option>
            <option value="not_validated">No validado</option>
          </select>
          <select aria-label="Filtrar por producto estrella" className="rounded-md border border-input bg-background px-3 py-2"
            value={starFilter} onChange={(event) => setStarFilter(event.target.value)}>
            <option value="">Producto estrella: todos</option>
            <option value="yes">Estrella</option>
            <option value="no">Sin estrella</option>
          </select>
          <select aria-label="Filtrar por tipo de prenda" className="rounded-md border border-input bg-background px-3 py-2"
            value={garmentTypeFilter} onChange={(event) => setGarmentTypeFilter(event.target.value)}>
            <option value="">Todos los tipos de prenda</option>
            <option value="none">Sin tipo de prenda</option>
            {garmentTypes.map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}
          </select>
          <Button type="button" variant="outline" onClick={() => setCatalogFilters({ searchTerm, status: statusFilter, temporada: temporadaFilter, codFam: familyFilter, sheetStage: sheetStageFilter, starFilter, garmentType: garmentTypeFilter })}>
            Imprimir catálogo
          </Button>
          <Button type="button" variant="outline" onClick={() => setMagnetFilters({ searchTerm, status: statusFilter, temporada: temporadaFilter, codFam: familyFilter, sheetStage: sheetStageFilter, starFilter, garmentType: garmentTypeFilter })}>
            Imprimir etiquetas imán
          </Button>
          <Button type="button" onClick={openNew} disabled={seasonsLoading}>
            <Plus className="mr-2 h-4 w-4" />
            Nuevo articulo
          </Button>
        </form>
        {hasListFilters && (
          <div className="mt-2 flex justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clearListFilters}
            >
              <X className="mr-2 h-4 w-4" />
              Limpiar filtros
            </Button>
          </div>
        )}
      </div>

      <div className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
          {loading ? (
            <p className="text-sm text-gray-500">Cargando articulos...</p>
          ) : !appliedFilters ? (
            <p className="text-sm text-gray-500">Pulsa Aplicar para buscar artículos con los filtros seleccionados.</p>
          ) : products.length === 0 ? (
            <p className="text-sm text-gray-500">No hay articulos de fabricacion.</p>
          ) : (
            products.map((product) => {
              const mainImage = getMainImage(product);
              const familyTariff = Number(product.family?.tarifa);
              const familyPriceField = Number.isInteger(familyTariff)
                ? LEGACY_SALE_PRICE_FIELDS[familyTariff - 1]
                : null;
              const costSheet = productCostSheet(product);
              const isValidated = costSheet?.validation_status === "validated";
              const selectedStage = appliedFilters?.sheetStage;
              const missingChecks = pendingChecks(costSheet, selectedStage, product.pending_workflow_issues);

              return (
                <div key={product.id} className="overflow-hidden rounded-lg border bg-white shadow-sm">
                  <button
                    type="button"
                    className="flex h-48 w-full items-center justify-center bg-gray-100"
                    onClick={() => mainImage && setImagePreview({ src: mainImage, alt: product.description })}
                    disabled={!mainImage}
                  >
                    {mainImage ? (
                      <img src={mainImage} alt={product.description} className="h-full w-full object-contain" />
                    ) : (
                      <div className="flex flex-col items-center gap-2 text-gray-400">
                        <ImageIcon className="h-10 w-10" />
                        <span className="text-sm">Sin imagen</span>
                      </div>
                    )}
                  </button>
                  <div className="space-y-3 p-4">
                    <div>
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="flex items-center gap-2 font-semibold">
                          <Star aria-label={product.producto_estrella ? "Producto estrella" : "Sin estrella"}
                            className={`h-5 w-5 shrink-0 ${product.producto_estrella ? "fill-amber-400 text-amber-500" : "text-gray-400"}`} />
                          {product.code}
                        </h3>
                        <span className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-700">
                          {statusLabels[product.status] || product.status}
                        </span>
                      </div>
                      <p className="mt-1 line-clamp-2 text-sm text-gray-600">{product.description}</p>
                      {isValidated && (
                        <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800" title="Escandallo validado">
                          <Lock className="h-3 w-3" aria-hidden="true" />
                          Validado
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      {product.season?.descrip || product.temporada || "Sin temporada"}
                      {product.family?.descrip ? ` · ${product.family.descrip}` : ""}
                      {product.composicion ? ` · ${product.composicion}` : ""}
                      {product.garment_type?.name ? ` · ${product.garment_type.name}` : ""}
                    </div>
                    <div className="rounded bg-gray-50 px-2 py-1 text-sm font-medium text-gray-700">
                      {familyPriceField
                        ? `Tarifa ${familyTariff}: ${formatMoney(product[familyPriceField])}`
                        : "Sin tarifa asignada a la familia"}
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" title="Observaciones" aria-label={`Observaciones de ${product.code}`} onClick={() => openObservations(product)}>
                        <MessageSquare className={`h-4 w-4 ${product.observaciones ? "text-blue-600" : ""}`} />
                      </Button>
                      <Button variant="outline" size="sm" title="Duplicar" disabled={seasonsLoading} onClick={() => openDuplicate(product)}>
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" title="Editar" onClick={() => edit(product)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="destructive" size="sm" title="Eliminar" onClick={() => remove(product)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {selectedStage && !isValidated && (
                    <section aria-label={`Pendientes de ${product.code}`} className="border-t border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                      {selectedStage === 'not_validated' ? <>
                        <p className="font-medium">No validado</p>
                        <p className="mt-1">{pendingStages(costSheet).length
                          ? `Estados pendientes: ${pendingStages(costSheet).map(stage => WORKFLOW_LABELS[stage]).join(' · ')}`
                          : 'Procesos completados. Pendiente de validación.'}</p>
                      </> : <>
                        <p className="font-medium">Pendiente de {WORKFLOW_LABELS[selectedStage]}</p>
                        {!costSheet && <p className="mt-1">Sin escandallo. Abre la ficha para crearlo.</p>}
                        <ul className="mt-2 list-disc space-y-1 pl-5">{missingChecks.map((label, index) => <li key={`${index}-${label}`}>{label}</li>)}</ul>
                      </>}
                    </section>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="flex items-center justify-end gap-3"><Button variant="outline" disabled={!page || loading} onClick={() => setPage((p) => p - 1)}>Anterior</Button><span>Página {page + 1}</span><Button variant="outline" disabled={!hasMore || loading} onClick={() => setPage((p) => p + 1)}>Siguiente</Button></div>
      <Dialog open={editorOpen} onOpenChange={handleEditorOpenChange}>
        <DialogContent className="max-h-[92vh] max-w-[96vw] overflow-y-auto p-5 xl:max-w-7xl">
          <DialogHeader className="pr-8">
            <DialogTitle>{editingId ? "Editar articulo de fabricacion" : "Nuevo articulo de fabricacion"}</DialogTitle>
            <DialogDescription>
              {editingId
                ? "Datos generales, imagenes y definición de procesos con sus materiales. La cruz y Guardar persisten la ficha."
                : "Indica familia, temporada y descripción y pulsa Crear para generar el artículo y su escandallo."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <form onSubmit={submit} className="rounded-lg border bg-white p-4 shadow-sm">
              <div className="mb-4 flex items-center justify-between gap-3">
                <h2 className="text-lg font-semibold">Datos del articulo</h2>
                <div className="flex items-center gap-2">
                  {sheetValidated && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-900">
                      <Lock className="h-3 w-3" />
                      Validado
                    </span>
                  )}
                  {editingId && (
                    <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800">
                      Guardado
                    </span>
                  )}
                </div>
              </div>

              {sheetValidated && (
                <div className="mb-4 flex gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
                  <Lock className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    El escandallo está validado. La ficha no se puede modificar. Pulsa Validación e introduce la
                    contraseña para desbloquearla.
                  </span>
                </div>
              )}

              <fieldset disabled={sheetValidated} className="min-w-0 border-0 p-0 disabled:opacity-70">
              <div className="grid gap-3 lg:grid-cols-4">
                <select
                  required={!editingId}
                  className="rounded-md border border-input bg-background px-3 py-2"
                  value={formData.cod_fam}
                  onChange={(event) => changeFormFamily(event.target.value)}
                >
                  <option value="">{editingId ? "Sin familia" : "Selecciona familia"}</option>
                  {families.map((family) => (
                    <option key={family.cod_fam} value={family.cod_fam.trim()}>
                      {family.cod_fam.trim()} · {family.descrip}
                    </option>
                  ))}
                </select>
                <Input
                  required
                  readOnly={!editingId}
                  placeholder={!editingId && newCodeLoading ? "Generando codigo..." : "Codigo"}
                  value={formData.code}
                  onChange={(event) => editingId && setFormData({ ...formData, code: event.target.value })}
                  className={!editingId ? "bg-gray-50 font-mono" : undefined}
                />
                <Input
                  required
                  className="lg:col-span-2"
                  placeholder="Descripcion"
                  value={formData.description}
                  onChange={(event) => setFormData({ ...formData, description: event.target.value })}
                />
                {editingId && <select
                  className="rounded-md border border-input bg-background px-3 py-2"
                  value={formData.status}
                  onChange={(event) => setFormData({ ...formData, status: event.target.value })}
                >
                  <option value="active">Activo</option>
                  <option value="obsolete">Obsoleto</option>
                </select>}
                <SeasonSelect data-season-selector="true"
                  required={!editingId}
                  className="rounded-md border border-input bg-background px-3 py-2"
                  value={formData.temporada}
                  onChange={(event) => changeFormSeason(event.target.value)}
                >
                  <option value="">{editingId ? "Sin temporada" : "Selecciona temporada"}</option>
                  {seasons.map((season) => (
                    <option key={season.cod_tem} value={season.cod_tem.trim()}>
                      {season.cod_tem.trim()} · {season.descrip}
                    </option>
                  ))}
                </SeasonSelect>
                <label className="space-y-1 text-sm lg:col-span-4">
                  <span className="font-medium text-gray-700">Tipo de prenda</span>
                  <select aria-label="Tipo de prenda" className="block w-full rounded-md border border-input bg-background px-3 py-2"
                    value={formData.garment_type_id} onChange={(event) => setFormData({ ...formData, garment_type_id: event.target.value })}>
                    <option value="">Sin tipo de prenda</option>
                    {garmentTypes.map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}
                  </select>
                </label>
                {editingId && <>
                <div className="grid gap-3 md:grid-cols-2 lg:col-span-4">
                <label className="min-w-0 space-y-1 text-sm">
                  <span className="font-medium text-gray-700">Composición</span>
                <textarea
                  className="min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  placeholder="Composicion"
                  value={formData.composicion}
                  onChange={(event) => setFormData({ ...formData, composicion: event.target.value })}
                />
                </label>
                <label className="min-w-0 space-y-1 text-sm">
                  <span className="font-medium text-gray-700">Observaciones del producto</span>
                  <textarea
                    className="min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    placeholder="Anota cómo ha resultado el producto y cualquier comentario"
                    value={formData.observaciones}
                    onChange={(event) => setFormData({ ...formData, observaciones: event.target.value })}
                  />
                </label>
                </div>
                <label className="flex items-center gap-2 text-sm lg:col-span-4">
                  <input type="checkbox" checked={formData.producto_estrella} onChange={(event) => setFormData({ ...formData, producto_estrella: event.target.checked })} />
                  <Star className={`h-5 w-5 ${formData.producto_estrella ? "fill-amber-400 text-amber-500" : "text-gray-400"}`} />
                  Producto estrella
                </label>
                <div className="grid gap-3 sm:grid-cols-[14rem_minmax(0,1fr)] lg:col-span-4">
                <label className="min-w-0 space-y-1 text-sm">
                  <span className="font-medium text-gray-700">Patrón de referencia</span>
                  <Input maxLength={20} placeholder="Patrón de referencia" value={formData.codigo_patron} onChange={(event) => setFormData({ ...formData, codigo_patron: event.target.value })} />
                </label>
                <div className="min-w-0 space-y-1 text-sm">
                  <label htmlFor="pattern-location" className="font-medium text-gray-700">Ubicación patrón</label>
                  <Input
                    id="pattern-location"
                    placeholder="URL, ruta local o carpeta compartida del patrón"
                    value={formData.ubicacion_patron}
                    onChange={(event) => setFormData({ ...formData, ubicacion_patron: event.target.value })}
                  />
                  <span className="block text-xs font-normal text-gray-500">
                    Ubicación del patrón. No se suben archivos.
                  </span>
                  {!sheetValidated && <PatternLocationActions value={formData.ubicacion_patron} />}
                </div>
                </div>
                </>}
              </div>
              {!editingId && (
                <div className="mt-2 flex flex-col gap-2 text-xs text-gray-500 sm:flex-row sm:items-center sm:justify-between">
                  <span>
                    {formData.cod_fam && formData.temporada
                      ? newCodeLoading
                        ? "Generando codigo automatico..."
                        : formData.code
                          ? `Codigo automatico asignado: ${formData.code}`
                          : "No se pudo asignar un codigo automatico."
                      : "Selecciona familia y temporada para asignar el código automáticamente. Al guardar se generará el escandallo con su plantilla."}
                  </span>
                  {formData.cod_fam && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={newCodeLoading || !formData.temporada}
                      onClick={() => generateNewProductCode(formData.cod_fam)}
                    >
                      Regenerar codigo
                    </Button>
                  )}
                </div>
              )}

              {editingId && (
              <>
              <div className="mt-4 rounded-md border p-3">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <h3 className="font-medium">Precios de Venta de Tarifas</h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!editingId || sheetValidated || recalculating}
                    onClick={recalculatePrices}
                  >
                    <RefreshCw className={`mr-2 h-4 w-4 ${recalculating ? "animate-spin" : ""}`} />
                    {recalculating ? "Recalculando..." : "Recalcular"}
                  </Button>
                </div>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
                  {LEGACY_SALE_PRICE_FIELDS.map((field, index) => {
                    const tarifa = index + 1;
                    const isFamilyTariff = highlightedTariff === tarifa;
                    const mismatch = mismatchByTariff[tarifa];
                    return (
                      <div
                        key={field}
                        className={`space-y-1 rounded-md p-2 text-sm ${isFamilyTariff ? "bg-emerald-50 ring-1 ring-emerald-300" : ""}`}
                      >
                        <div className="flex items-center justify-between gap-1">
                          <span className={`font-medium ${isFamilyTariff ? "text-emerald-800" : "text-gray-700"}`}>
                            {salePriceLabels[index]}
                          </span>
                          {mismatch && (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button
                                  type="button"
                                  className="rounded p-0.5 text-amber-600 hover:bg-amber-100"
                                  aria-label={`Aviso tarifa ${tarifa}`}
                                >
                                  <AlertTriangle className="h-4 w-4" />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent align="end" className="w-80 space-y-3">
                                <div className="space-y-1 text-sm">
                                  <p className="font-medium text-gray-900">Precio desfasado</p>
                                  <p className="text-gray-600">
                                    El valor guardado no coincide con el recálculo actual (escandallo o reglas de familia).
                                  </p>
                                  <p>Actual: {formatMoney(mismatch.current)}</p>
                                  <p>Esperado: {formatMoney(mismatch.expected)}</p>
                                </div>
                                <div className="flex flex-col gap-2">
                                  <Button
                                    type="button"
                                    size="sm"
                                    disabled={sheetValidated || recalculating}
                                    onClick={() => updateSingleTariff(tarifa)}
                                  >
                                    Actualizar esta tarifa
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    disabled={sheetValidated || recalculating}
                                    onClick={recalculatePrices}
                                  >
                                    Recalcular las 6
                                  </Button>
                                </div>
                              </PopoverContent>
                            </Popover>
                          )}
                        </div>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData[field]}
                          className={isFamilyTariff
                            ? mismatch
                              ? "border-red-400 bg-red-50 text-red-700 focus-visible:ring-red-400"
                              : "border-emerald-300 bg-emerald-50"
                            : ""}
                          onChange={(event) => setFormData({ ...formData, [field]: event.target.value })}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="mt-4 space-y-3 rounded-md border p-3">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <h3 className="font-medium">Imagenes del articulo</h3>
                    <p className="text-xs text-gray-500">La imagen marcada con estrella se muestra en el listado.</p>
                  </div>
                  <input id="mfg-product-image-upload" type="file" accept="image/*" className="hidden" onChange={uploadImage} />
                  <Button type="button" variant="outline" onClick={() => document.getElementById("mfg-product-image-upload")?.click()}>
                    <Plus className="mr-2 h-4 w-4" />
                    Añadir imagen
                  </Button>
                </div>

                <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-6">
                  {formData.images.map((image, index) => (
                    <div key={`${image.slice(0, 24)}-${index}`} className="group relative overflow-hidden rounded border">
                      <img src={image} alt={`Imagen ${index + 1}`} className="h-32 w-full object-cover" />
                      <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
                        <Button type="button" variant="ghost" size="icon" onClick={() => setMainImage(index)}>
                          {index === formData.main_image_index ? <Star className="h-4 w-4 text-yellow-300" /> : <StarOff className="h-4 w-4 text-white" />}
                        </Button>
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeImage(index)}>
                          <Trash2 className="h-4 w-4 text-white" />
                        </Button>
                      </div>
                      {index === formData.main_image_index && (
                        <span className="absolute left-2 top-2 rounded bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-900">
                          Principal
                        </span>
                      )}
                    </div>
                  ))}
                  {formData.images.length === 0 && (
                    <div className="rounded border border-dashed p-6 text-center text-sm text-gray-500 sm:col-span-2 md:col-span-4 xl:col-span-6">
                      Sin imagenes asociadas.
                    </div>
                  )}
                </div>
              </div>
              </>
              )}
              </fieldset>
              {sheetValidated && formData.ubicacion_patron && <div className="mt-3"><PatternLocationActions value={formData.ubicacion_patron} /></div>}

              <div className="mt-4 flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => handleEditorOpenChange(false)} disabled={saving}>
                  <X className="mr-2 h-4 w-4" />
                  {editingId ? "Guardar y salir" : "Cerrar"}
                </Button>
                <Button type="submit" disabled={sheetValidated || saving || (!editingId && newCodeLoading)}>
                  <Save className="mr-2 h-4 w-4" />
                  {!editingId ? (newCodeLoading ? "Generando codigo..." : saving ? "Creando..." : "Crear") : saving ? "Guardando..." : "Guardar"}
                </Button>
              </div>
            </form>

            {editingId ? (
              <div className="space-y-6">
                <CostSheetManager
                  productId={editingId}
                  beforeCloseRef={beforeCloseRef}
                  hideProductSelector
                  reloadKey={productRevision}
                  onBeforeValidate={persistProduct}
                  onValidationChange={handleValidationChange}
                  onTotalsChange={setSheetTotalCost}
                  onAutomaticPrices={applyAutomaticPrices}
                />
                <ProductSimulationsGrid productId={editingId} reloadKey={productRevision} />
              </div>
            ) : (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                Completa familia, temporada y descripción y pulsa Crear. El escandallo de la plantilla de familia aparecerá a continuación.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(imagePreview)} onOpenChange={(open) => !open && setImagePreview(null)}>
        <DialogContent className="max-w-[90vw] bg-black p-2 md:max-w-3xl">
          {imagePreview && (
            <div className="flex h-full w-full items-center justify-center">
              <img src={imagePreview.src} alt={imagePreview.alt} className="max-h-[80vh] max-w-full object-contain" draggable={false} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(observationsTarget)} onOpenChange={(open) => !open && !savingObservations && setObservationsTarget(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Observaciones del producto</DialogTitle>
            <DialogDescription>{observationsTarget?.product?.code} · Anota cómo ha resultado el producto y cualquier comentario.
            </DialogDescription>
          </DialogHeader>
          <textarea aria-label="Observaciones del producto" className="min-h-[220px] w-full rounded-md border border-input p-3 text-sm"
            value={observationsDraft} onChange={(event) => setObservationsDraft(event.target.value)} disabled={savingObservations} />
          <div className="flex justify-end gap-2">
            <Button variant="outline" disabled={savingObservations} onClick={() => setObservationsTarget(null)}>Cancelar</Button>
            <Button disabled={savingObservations} onClick={saveObservations}>{savingObservations ? "Guardando..." : "Guardar observaciones"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(duplicateSource)} onOpenChange={(open) => !open && closeDuplicate()}>
        <DialogContent className={duplicateStep === "review" ? "max-w-2xl" : "max-w-lg"}>
          <DialogHeader>
            <DialogTitle>{duplicateStep === "review" ? "Revisar duplicación" : "Duplicar artículo"}</DialogTitle>
            <DialogDescription>
              {duplicateStep === "review"
                ? `Revisa cómo se aplicará el escandallo de "${duplicateSource?.code || ""}" a la familia destino.`
                : `Se creará una copia de${duplicateSource ? ` "${duplicateSource.code} - ${duplicateSource.description}"` : ""} (imágenes y escandallo incluidos) como artículo activo con un código nuevo.`}
            </DialogDescription>
          </DialogHeader>

          {duplicateStep === "review" ? (
            <div className="space-y-4">
              <DuplicateIssueGroups issues={duplicatePreview?.issues || []} />
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDuplicateStep("form")} disabled={duplicating}>
                  Atrás
                </Button>
                <Button type="button" onClick={confirmDuplicate} disabled={duplicating}>
                  <Copy className="mr-2 h-4 w-4" />
                  {duplicating ? "Duplicando..." : "Duplicar de todos modos"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <label className="block space-y-1 text-sm">
                <span className="font-medium text-gray-700">Temporada (obligatoria)</span>
                <SeasonSelect required value={duplicateSeason} onChange={(event) => changeDuplicateSeason(event.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2">
                  <option value="">Selecciona temporada</option>
                  {seasons.map((season) => <option key={season.cod_tem} value={season.cod_tem.trim()}>{season.cod_tem.trim()} · {season.descrip}</option>)}
                </SeasonSelect>
              </label>
              <label className="flex items-center gap-2 rounded-md border p-3 text-sm">
                <input
                  type="checkbox"
                  checked={duplicateSpecialSizes}
                  disabled={duplicateCodeLoading || !duplicateSeason || duplicateKeepReference}
                  onChange={(event) => toggleDuplicateSpecialSizes(event.target.checked)}
                />
                <span>
                  <span className="font-medium">Tallas especiales</span>
                  <span className="mt-0.5 block text-xs text-gray-600">
                    El código será {duplicateSource && duplicateSeason ? specialSizesProductCode(duplicateSource.code, duplicateSource.temporada, duplicateSeason) : "código/E + temporada"} y no se podrá cambiar de familia.
                  </span>
                </span>
              </label>
              <label className="flex items-center gap-2 rounded-md border p-3 text-sm">
                <input type="checkbox" checked={duplicateKeepReference}
                  disabled={duplicateSpecialSizes || duplicateCodeLoading || !canPreserveDuplicateReference(duplicateSource?.cod_fam, duplicateCodFam)}
                  onChange={(event) => toggleDuplicateKeepReference(event.target.checked)} />
                <span>
                  <span className="font-medium">Conservar referencia antigua</span>
                  <span className="mt-0.5 block text-xs text-gray-600">
                    {duplicateSource && duplicateSeason
                      ? `El código será ${preservedReferenceProductCode(duplicateSource.code, families.find((family) => family.cod_fam.trim() === duplicateSource.cod_fam?.trim()), duplicateSeason)}.`
                      : "Selecciona temporada para componer el código."} Solo disponible en la familia de origen. No se puede combinar con tallas especiales.
                  </span>
                </span>
              </label>
              <label className="block space-y-1 text-sm">
                <span className="font-medium text-gray-700">Familia</span>
                <select
                  className="w-full rounded-md border border-input bg-background px-3 py-2"
                  value={duplicateCodFam}
                  disabled={duplicateSpecialSizes || duplicateKeepReference}
                  onChange={(event) => changeDuplicateFamily(event.target.value)}
                >
                  <option value="">Sin familia</option>
                  {families.map((family) => (
                    <option key={family.cod_fam} value={family.cod_fam.trim()}>
                      {family.cod_fam.trim()} · {family.descrip}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block space-y-1 text-sm">
                <span className="font-medium text-gray-700">Nuevo código</span>
                <div className="flex gap-2">
                  <Input
                    placeholder={duplicateCodeLoading ? "Generando..." : "Código del nuevo artículo"}
                    value={duplicateCode}
                    onBlur={() => !duplicateSpecialSizes && !duplicateKeepReference && setDuplicateCode(productCodeWithSeason(duplicateCode, duplicateSeason))}
                    readOnly={duplicateSpecialSizes || duplicateKeepReference || !duplicateSeason || duplicateCodeLoading}
                    className={duplicateSpecialSizes || duplicateKeepReference ? "bg-gray-50 font-mono" : undefined}
                    onChange={(event) => !duplicateSpecialSizes && !duplicateKeepReference && duplicateSeason && !duplicateCodeLoading && setDuplicateCode(event.target.value)}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    disabled={duplicateSpecialSizes || duplicateKeepReference || !duplicateCodFam || !duplicateSeason || duplicateCodeLoading}
                    onClick={() => generateDuplicateCode(duplicateCodFam)}
                  >
                    Regenerar
                  </Button>
                </div>
              </label>

              {!duplicateCodFam && (
                <p className="rounded-md border border-yellow-200 bg-yellow-50 p-3 text-xs text-yellow-900">
                  Selecciona una familia para generar un código automático, o escribe uno manualmente. Sin familia se copia el escandallo tal cual.
                </p>
              )}

              {duplicateSource?.cod_fam?.trim() !== duplicateCodFam.trim() && duplicateCodFam && (
                <div className="space-y-1 rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
                  <p>Al cambiar de familia se aplican las marcas de la plantilla de origen:</p>
                  <ul className="list-disc space-y-1 pl-4">
                    <li>{DUPLICATE_FAMILY_RULES.membership}</li>
                    <li>{DUPLICATE_FAMILY_RULES.fixed}</li>
                    <li>{DUPLICATE_FAMILY_RULES.destination}</li>
                    <li>{DUPLICATE_FAMILY_RULES.fabric}</li>
                    <li>{DUPLICATE_FAMILY_RULES.material}</li>
                  </ul>
                  <p>Se recalculan las tarifas con el escandallo resultante.</p>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={closeDuplicate} disabled={duplicating || duplicatePreviewLoading}>
                  <X className="mr-2 h-4 w-4" />
                  Cancelar
                </Button>
                <Button type="button" onClick={startDuplicate} disabled={duplicating || !duplicateSeason || !duplicateCode.trim() || duplicateCodeLoading || duplicatePreviewLoading}>
                  <Copy className="mr-2 h-4 w-4" />
                  {duplicatePreviewLoading ? "Revisando..." : duplicating ? "Duplicando..." : "Crear duplicado"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
